import { FileRole, type Prisma } from "@prisma/client";
import { CAD_SATISFYING_FILE_TAGS } from "@remi/constants";
import { FileTag } from "@remi/enums/file-tag";
import type { TaskStatus } from "@remi/enums/task-status";
import { TaskType } from "@remi/enums/task-type";
import { isOneOf } from "@remi/ts-utils";
import { actionableStatuses } from "@remi/types/tasks";

import { hasCapability, type User } from "../base";
import { NON_EXISTENT_ID } from "../utils/types";
import { complianceEvent } from "./complianceEvent";

const canUpdateFile = (
  user: User,
  file?: {
    personId: string | null;
    person?: { organizationId: string } | null;
    task?: { type: TaskType; status: TaskStatus } | null;
  },
) => {
  if (!file) {
    return false;
  }
  if (hasCapability(user, "file.update")) {
    return true;
  }
  if (hasCapability(user, "file.update.owned") && user.person.id === file.personId) {
    return true;
  }
  if (hasCapability(user, "file.update.contractorTaskUpload")) {
    const task = file.task;
    if (task === null || task === undefined) {
      return false;
    }
    const taskConditionsMet =
      task.type === TaskType.UPLOADED_COMPLETED_WORK_PHOTOS && isOneOf(task.status, actionableStatuses);
    const isInSameOrg =
      file.person?.organizationId != null && user.person.organizationId === file.person.organizationId;
    return taskConditionsMet && isInSameOrg;
  }
  return false;
};

export const file = {
  update: canUpdateFile,

  // file.update gates deletion, contractor-document rejection and version history as well as
  // metadata, so tagging gets its own capability for roles that should tag but not delete.
  updateTag: (user: User, file?: Parameters<typeof canUpdateFile>[1]) => {
    if (!file) {
      return false;
    }
    return canUpdateFile(user, file) || hasCapability(user, "file.update.tag");
  },

  approveTag: (user: User) => hasCapability(user, "file.approveTag"),

  // `update` denies when it is handed no file, so it cannot gate a create. The file type decides
  // where an upload lands, and so whether a contractor can see it, so each type has its own gate.
  uploadCad: (user: User) => hasCapability(user, "file.upload.cad"),
};

const getConversationAccess = (organizationId: string) => ({
  OR: [
    {
      organizations: {
        some: {
          organizationId,
        },
      },
    },
    {
      persons: {
        some: {
          person: {
            organizationId,
          },
        },
      },
    },
  ],
});

const messageWhere = (organizationId: string) => ({
  OR: [
    {
      message: {
        OR: [
          {
            organizationId,
          },
          {
            conversationItem: {
              conversation: getConversationAccess(organizationId),
            },
          },
        ],
        deletedAt: null,
      },
    },
    {
      smsMessage: {
        conversationItem: {
          conversation: getConversationAccess(organizationId),
        },
      },
    },
    {
      email: {
        conversationItem: {
          conversation: getConversationAccess(organizationId),
        },
        deletedAt: null,
      },
    },
    { messageId: null, smsMessageId: null, emailId: null },
  ],
});

// Critical carve-outs these scope builders preserve:
//   - estimator/owned SKIP messageWhere. ESTIMATOR is REMI_INTERNAL org
//     and FIELD_WORKER is link-authenticated with organizationId =
//     remi_internal; both make messageWhere an ineffective scope boundary
//     (remi_internal is on every conversation). Prior BONES-249 fix.
//   - contractor excludes files named 'quote*' and any file with a
//     bidQuoteForPdf or quoteVersionForPdf relation — hides internal
//     quote PDFs from contractors.
//   - adjuster is restricted to insurance-tagged files, not the broader
//     messageWhere scope applied to other roles.

const adjusterScope = (): Prisma.FileWhereInput => ({
  tags: { some: { tagId: { in: [FileTag.XACTIMATE_QUOTE, FileTag.HIC] } } },
});

const estimatorScope = (user: User): Prisma.FileWhereInput => ({
  OR: [
    { tags: { some: { tagId: { in: [FileTag.XACTIMATE_QUOTE, FileTag.ITEL_REPORT, FileTag.SITE_AUDIT] } } } },
    {
      role: {
        in: [
          FileRole.EAGLEVIEW,
          FileRole.EAGLEVIEW_ESX,
          FileRole.MEASUREMENT_REPORT,
          FileRole.SKETCH,
          FileRole.QUOTE_IMAGE,
          FileRole.WORK_ORDER,
        ],
      },
    },
    { role: null },
    { personId: user.person.id },
  ],
});

const ownedScope = (user: User): Prisma.FileWhereInput => ({
  OR: [{ project: { owners: { some: { personId: user.person.id, deletedAt: null } } } }, { personId: user.person.id }],
});

// Dual-role users (e.g. CONTRACTOR_ADMIN + FIELD_WORKER) are handled
// naturally by the additive accumulator in fileWhere() — ownedScope and
// contractorScope are both pushed to `accessScopes` and OR-composed. This
// replaces the pre-BONES-185 early-return that clipped such users to
// owned-only.
const homeownerScope = (user: User): Prisma.FileWhereInput => ({
  AND: [
    messageWhere(user.person.organizationId),
    {
      OR: [
        {
          bidQuoteForImage: {
            some: {
              requestForQuote: { bid: { workSite: { homeownerId: user.person.id } } },
            },
          },
        },
        {
          bidQuoteForPdf: {
            requestForQuote: { bid: { workSite: { homeownerId: user.person.id } } },
          },
        },
        {
          quoteVersionForImage: {
            some: {
              requestForQuote: { bid: { workSite: { homeownerId: user.person.id } } },
            },
          },
        },
        {
          quoteVersionForPdf: {
            requestForQuote: { bid: { workSite: { homeownerId: user.person.id } } },
          },
        },
      ],
    },
  ],
});

const contractorScope = (user: User): Prisma.FileWhereInput => ({
  AND: [
    messageWhere(user.person.organizationId),
    fileOrgBinding(user.person.organizationId),
    {
      OR: [
        {
          role: {
            in: [
              FileRole.EAGLEVIEW,
              FileRole.HOVER_REPORT,
              FileRole.SKETCH,
              FileRole.QUOTE_IMAGE,
              FileRole.WORK_ORDER,
              // UPLOADED_COMPLETED_WORK_PHOTOS_CONTRACTOR posts these into the contractor's
              // own conversation, so omitting the role hides a file we deliberately sent them.
              FileRole.CORRECTIONS_PHOTO,
            ],
          },
        },
        // Crews read only the report PDF; ESX, geometry and measurement data are estimating inputs.
        { role: FileRole.MEASUREMENT_REPORT, tags: { some: { tagId: FileTag.PDF_REPORT } } },
        { personId: user.person.id },
        { role: null },
        { contractorProfileId: user.person.organizationId },
      ],
    },
    {
      OR: [
        {
          AND: [{ tags: { none: { tagId: FileTag.FSD_PORTAL } } }, { tags: { none: { tagId: FileTag.HIC } } }],
        },
        { tags: { some: { tagId: { in: [...CAD_SATISFYING_FILE_TAGS] } } } },
      ],
    },
    { name: { not: { contains: "quote" } } },
    { bidQuoteForPdf: null },
    { quoteVersionForPdf: null },
  ],
});

const buyerScope = (user: User): Prisma.FileWhereInput => ({
  AND: [
    messageWhere(user.person.organizationId),
    fileOrgBinding(user.person.organizationId),
    {
      tags: { none: { tagId: FileTag.SITE_AUDIT } },
      OR: [{ role: { not: FileRole.WORK_ORDER } }, { role: null }],
    },
  ],
});

// Contractors join a bid's work projects, never its BID project, and quote images carry no
// projectId/workSiteId — binding only on bidProject hides every roof image from crews.
const quoteImageBidParticipation = (organizationId: string) => {
  const getParticipation = () => ({ organizations: { some: { organizationId, deletedAt: null } } });
  return {
    requestForQuote: {
      bid: { OR: [{ bidProject: getParticipation() }, { childProjects: { some: getParticipation() } }] },
    },
  };
};

// OR over relationships, never AND over anchors: contractorProfileId files carry no
// projectId/workSiteId, so requiring an anchor hides contractors' own documents (ODD-7758).
export const fileOrgBinding = (organizationId: string): Prisma.FileWhereInput => ({
  OR: [
    { project: { organizations: { some: { organizationId, deletedAt: null } } } },
    { workSite: { projects: { some: { organizations: { some: { organizationId, deletedAt: null } } } } } },
    { contractorProfileId: organizationId },
    { person: { organizationId } },
    { bidRequest: { buyerOrganizationId: organizationId } },
    { message: { conversationItem: { conversation: getConversationAccess(organizationId) } } },
    { smsMessage: { conversationItem: { conversation: getConversationAccess(organizationId) } } },
    { email: { conversationItem: { conversation: getConversationAccess(organizationId) } } },
    { quoteVersionForImage: { some: quoteImageBidParticipation(organizationId) } },
    { bidQuoteForImage: { some: quoteImageBidParticipation(organizationId) } },
  ],
});

// Superseded work orders carry pricing the contractor no longer works to; only the version they
// are being asked to accept, or already accepted, stays readable.
const currentWorkOrderVersion: Prisma.FileWhereInput = {
  OR: [
    { role: { not: FileRole.WORK_ORDER } },
    { role: null },
    {
      workOrder: {
        is: { OR: [{ projectWithActiveWorkOrder: { is: {} } }, { projectWithAcceptedWorkOrder: { is: {} } }] },
      },
    },
  ],
};

const profilePhotoScope: Prisma.FileWhereInput = { profilePhotoPerson: { is: {} } };

export const fileWhere = (user: User): Prisma.FileWhereInput => {
  const hasFullFileReadAccess = hasCapability(user, "file.read.all");

  // Internal ops (file.read.all) can see soft-deleted WORK_ORDER files so they
  // can reference prior work orders after contractor reassignment (ENFRANK-2259).
  // ENG-9747 soft-deletes rejected WO PDFs so the next contractor can't see
  // prior pricing — that guarantee stays; this widens visibility only for
  // internal roles. The WORK_ORDER exemption is ANDed with the complianceEventId
  // guard below, so a deleted WO with a compliance event still stays hidden
  // from non-compliance users — do not reorder these two clauses.
  //
  // baseFilters applied at the AND root so their indexes (isDeleted,
  // complianceEventId) gate row evaluation before the scope OR branches run.
  const baseFilters: Prisma.FileWhereInput[] = [
    hasFullFileReadAccess ? { OR: [{ isDeleted: false }, { role: FileRole.WORK_ORDER }] } : { isDeleted: false },
  ];
  if (!complianceEvent.read(user)) {
    baseFilters.push({ complianceEventId: null });
  }

  if (hasFullFileReadAccess) {
    return { AND: baseFilters };
  }

  // Captures of Sunrun's partner portal are internal quoting evidence; only file.read.all principals see them.
  baseFilters.push({ tags: { none: { tagId: FileTag.SUNRUN_PORTAL_SCREENSHOT } } });
  // A base filter, not a contractorScope clause: contractor crews also hold file.read.owned (FIELD_WORKER
  // via checklist links), and ownedScope opens every file on a project they are assigned to.
  baseFilters.push(currentWorkOrderVersion);

  const accessScopes: Prisma.FileWhereInput[] = [];
  if (hasCapability(user, "file.read.adjuster")) {
    accessScopes.push(adjusterScope());
  }
  if (hasCapability(user, "file.read.estimator")) {
    accessScopes.push(estimatorScope(user));
  }
  if (hasCapability(user, "file.read.owned")) {
    accessScopes.push(ownedScope(user));
  }
  if (hasCapability(user, "file.read.homeowner")) {
    accessScopes.push(homeownerScope(user));
  }
  if (hasCapability(user, "file.read.contractor")) {
    accessScopes.push(contractorScope(user));
  }
  if (hasCapability(user, "file.read.buyer")) {
    accessScopes.push(buyerScope(user));
  }

  if (accessScopes.length === 0) {
    // No access capability — profile-photo carve-out still applies (avatars
    // are shared-surface content), but no other file reads.
    return {
      AND: [...baseFilters, { OR: [{ AND: [{ id: NON_EXISTENT_ID }] }, profilePhotoScope] }],
    };
  }

  return {
    AND: [...baseFilters, { OR: [...accessScopes, profilePhotoScope] }],
  };
};
